import { cn } from '@/lib/utils';
import type { ExportPresetDefinition } from '@mss_creator/shared';

interface ExportPresetGlyphProps {
  presetId: ExportPresetDefinition['id'];
}

interface MiniPageProps {
  className: string;
  rows: number;
}

/** Stable keys for the decorative table lines — at most eight per sheet. */
const ROW_KEYS = ['r1', 'r2', 'r3', 'r4', 'r5', 'r6', 'r7', 'r8'] as const;

/** One sheet of paper, with a header band and `rows` thin table lines. */
function MiniPage({ className, rows }: MiniPageProps) {
  return (
    <div
      data-glyph-page
      className={cn(
        'flex flex-col gap-px overflow-hidden rounded-sm border border-gray-300 bg-white p-px',
        className,
      )}
    >
      <div className="w-full border-gray-400 border-t-2" />
      {ROW_KEYS.slice(0, rows).map((rowKey) => (
        <div key={rowKey} className="w-full border-gray-200 border-t" />
      ))}
    </div>
  );
}

/**
 * The miniature page silhouette on a preset card — pure CSS, so it follows the
 * PMS tokens and needs no asset. Each shape shows the *paper outcome* of the
 * preset rather than its settings: how many sheets, which way up, and — for
 * Atelier — that the sheets are meant to be joined into one poster.
 */
export function ExportPresetGlyph({ presetId }: ExportPresetGlyphProps) {
  return (
    <div
      aria-hidden="true"
      data-testid={`preset-glyph-${presetId}`}
      className="flex h-12 items-center justify-center gap-1"
    >
      {presetId === 'lecture' && (
        <>
          <MiniPage className="h-7 w-10" rows={3} />
          <MiniPage className="h-7 w-10" rows={3} />
        </>
      )}
      {presetId === 'vue-densemble' && <MiniPage className="h-8 w-12" rows={8} />}
      {presetId === 'atelier' && (
        // One joined block, not four separate sheets: the dashed lines are the
        // cut/assembly seams of a single A3 poster, which is the whole point
        // of the preset.
        <div
          data-glyph-page
          className="relative h-10 w-14 rounded-sm border border-gray-300 bg-white"
        >
          <div className="absolute inset-x-1 top-1 border-gray-400 border-t-2" />
          <div
            data-glyph-cut
            className="absolute inset-y-0 left-1/2 border-gray-400 border-l border-dashed"
          />
          <div
            data-glyph-cut
            className="absolute inset-x-0 top-1/2 border-gray-400 border-t border-dashed"
          />
        </div>
      )}
      {presetId === 'dossier' && (
        <>
          <MiniPage className="h-10 w-7" rows={5} />
          <MiniPage className="h-10 w-7" rows={5} />
        </>
      )}
    </div>
  );
}
